import Vue from 'vue';
import Router, { RouterOptions, Route } from 'vue-router';
import store from '@/stores/store';
import Home from '@/pages/home.vue';
import Login from '@/pages/login.vue';
import Logout from '@/pages/logout.vue';
import ChangePassword from '@/pages/change-password.vue';
import ForgotPassword from '@/pages/forgot-password.vue';
import NotFound from '@/pages/not-found.vue';
import Admin from '@/pages/admin.vue';
import CreateEnquiry from '@/components/create-enquiry.vue';
import MyEnquiries from '@/pages/my-enquiries.vue';
import MyEnquiryList from '@/components/my-enquiries.vue';
import Enquiries from '@/pages/enquiries.vue';
import EnquiryList from '@/components/enquiries.vue';
import ViewEnquiry from '@/components/view-enquiry.vue';
import Quotes from '@/pages/quotes.vue';
import QuoteList from '@/components/quotes.vue';
import ViewQuote from '@/components/view-quote.vue';
import CreateQuote from '@/components/create-quote.vue';
import Approvals from '@/pages/approvals.vue';
import ApprovalList from '@/components/approvals.vue';
import ViewApproval from '@/components/view-approval.vue';
import Orders from '@/pages/orders.vue';
import OrderList from '@/components/orders.vue';
import CreateOrder from '@/components/create-order.vue';
import ViewOrder from '@/components/view-order.vue';
import AdminContent from '@/components/admin-content.vue';
import AdminUsers from '@/components/admin-users.vue';
import Forbidden from '@/pages/forbidden.vue';
import AccessDenied from '@/pages/access-denied.vue';
import { Permissions } from '@/models/permission-constants';
import Reports from '@/components/reports/reports.vue';
import ReportList from '@/components/reports/report-list.vue';
import ViewReport from '@/components/reports/view-report.vue';
import { Customer } from '@/models/customer';
import { kebabcase } from '@/filters/kebab_case';

Vue.use(Router);

const routerOptions: RouterOptions = {
    mode: 'history',
    routes: [
        // Home
        {
            path: '/',
            name: 'home',
            component: Home,
            meta: {
                title: 'Home',
                requiresAuthentication: true,
            },
        },
        // Login
        {
            path: '/login',
            name: 'login',
            component: Login,
            meta: {
                title: 'Login',
                requiresAuthentication: false,
            },
        },
        // Logout
        {
            path: '/logout',
            name: 'logout',
            component: Logout,
            meta: {
                title: 'Logged out',
                requiresAuthentication: false,
            },
        },
        // Change password
        {
            path: '/change-password',
            name: 'change-password',
            component: ChangePassword,
            meta: {
                title: 'Change password',
                requiresAuthentication: false,
            },
        },
        // Forgot password
        {
            path: '/forgot-password',
            name: 'forgot-password',
            component: ForgotPassword,
            meta: {
                title: 'Forgot password',
                requiresAuthentication: false,
            },
        },
        // Enquiries
        {
            path: '/enquiries',
            name: 'enquiries',
            component: Enquiries,
            meta: {
                title: 'Enquiries',
                requiresAuthentication: true,
            },
            redirect: { name: 'enquiry-list' },
            children: [
                {
                    path: '',
                    name: 'enquiry-list',
                    component: EnquiryList,
                    meta: {
                        title: 'Enquiries',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.VIEW_ENQUIRIES,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                        ignoreBreadcrumb: true,
                    },
                },
                {
                    path: 'create',
                    name: 'create-enquiry',
                    component: CreateEnquiry,
                    meta: {
                        title: 'Create enquiry',
                        requiresAnyRole: [
                            Permissions.CREATE_ENQUIRIES,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                        requiresAuthentication: true,
                    },
                },
                {
                    path: ':id',
                    name: 'view-enquiry',
                    component: ViewEnquiry,
                    meta: {
                        title: 'Enquiry',
                        breadcrumbTitle: 'Enquiry',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.VIEW_ENQUIRIES,
                            Permissions.CREATE_ENQUIRIES,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                    beforeEnter: (to, from, next) => {
                        to.meta.title = `Enquiry - ${to.params.id}`;
                        to.meta.breadcrumbTitle = `${to.params.id}`;
                        next();
                    },
                }
            ],
        },
        // Quotes
        {
            path: '/quotes',
            name: 'quotes',
            component: Quotes,
            meta: {
                title: 'Quotes',
                requiresAuthentication: true,
            },
            redirect: { name: 'quote-list' },
            children: [
                {
                    path: '',
                    name: 'quote-list',
                    component: QuoteList,
                    meta: {
                        title: 'Quotes',
                        requiresAuthentication: true,
                        ignoreBreadcrumb: true,
                        requiresAnyRole: [
                            Permissions.VIEW_QUOTES,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                },
                {
                    path: 'create/:id',
                    name: 'create-quote',
                    component: CreateQuote,
                    meta: {
                        title: 'Create quote',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.CREATE_QUOTES,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                },
                {
                    path: ':id',
                    name: 'view-quote',
                    component: ViewQuote,
                    meta: {
                        title: 'Quote',
                        breadcrumbTitle: 'Quote',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.VIEW_QUOTES,
                            Permissions.CREATE_QUOTES,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                    beforeEnter: (to, from, next) => {
                        to.meta.title = `Quote - ${to.params.id}`;
                        to.meta.breadcrumbTitle = `${to.params.id}`;
                        next();
                    },
                }
            ],
        },
        // Approvals
        {
            path: '/approvals',
            name: 'approvals',
            component: Approvals,
            meta: {
                title: 'Approvals',
                requiresAuthentication: true,
            },
            redirect: { name: 'approval-list' },
            children: [
                {
                    path: '',
                    name: 'approval-list',
                    component: ApprovalList,
                    meta: {
                        title: 'Approvals',
                        requiresAuthentication: true,
                        ignoreBreadcrumb: true,
                        requiresAnyRole: [
                            Permissions.VIEW_APPROVALS,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                },
                {
                    path: ':id',
                    name: 'view-approval',
                    component: ViewApproval,
                    meta: {
                        title: 'Approval',
                        breadcrumbTitle: 'Approval',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.VIEW_APPROVALS,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                    beforeEnter: (to, from, next) => {
                        to.meta.title = `Approval - ${to.params.id}`;
                        to.meta.breadcrumbTitle = `${to.params.id}`;
                        next();
                    },
                }
            ],
        },
        // Orders
        {
            path: '/orders',
            name: 'orders',
            component: Orders,
            meta: {
                title: 'Orders',
                requiresAuthentication: true,
            },
            redirect: { name: 'order-list' },
            children: [
                {
                    path: '',
                    name: 'order-list',
                    component: OrderList,
                    meta: {
                        title: 'Orders',
                        requiresAuthentication: true,
                        ignoreBreadcrumb: true,
                        requiresAnyRole: [
                            Permissions.VIEW_ORDERS,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                },
                {
                    path: 'create/:id',
                    name: 'create-order',
                    component: CreateOrder,
                    meta: {
                        title: 'Create quote',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.CREATE_ORDERS,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                },
                {
                    path: ':id',
                    name: 'view-order',
                    component: ViewOrder,
                    meta: {
                        title: 'Order',
                        breadcrumbTitle: 'Order',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.VIEW_ORDERS,
                            Permissions.CREATE_ORDERS,
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                    beforeEnter: (to, from, next) => {
                        to.meta.title = `Order - ${to.params.id}`;
                        to.meta.breadcrumbTitle = `${to.params.id}`;
                        next();
                    },
                }
            ],
        },
        // My Enquiries
        {
            path: '/my-enquiries',
            name: 'my-enquiries',
            component: MyEnquiries,
            meta: {
                title: 'My enquiries',
                requiresAuthentication: true,
            },
            redirect: { name: 'my-enquiry-list' },
            children: [
                {
                    path: '',
                    name: 'my-enquiry-list',
                    component: MyEnquiryList,
                    meta: {
                        title: 'Enquiries',
                        requiresAuthentication: true,
                        ignoreBreadcrumb: true,
                    },
                }
            ],
        },
        // Admin
        {
            path: '/admin',
            name: 'admin',
            component: Admin,
            meta: {
                title: 'Admin',
                requiresAuthentication: true,
            },
            redirect: { name: 'admin-content' },
            children: [
                {
                    path: '',
                    name: 'admin-content',
                    component: AdminContent,
                    meta: {
                        title: 'Admin',
                        requiresAuthentication: true,
                        ignoreBreadcrumb: true,
                        requiresAnyRole: [
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                },
                {
                    path: 'users',
                    name: 'users',
                    component: AdminUsers,
                    meta: {
                        title: 'Users',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                },
                {
                    path: 'reports',
                    name: 'reports',
                    component: Reports,
                    redirect: { name: 'reports-list' },
                    meta: {
                        title: 'Reports',
                        requiresAuthentication: true,
                        requiresAnyRole: [
                            Permissions.SYSTEM,
                            Permissions.ADMIN
                        ],
                    },
                    children: [
                        {
                            path: 'reports-list',
                            name: 'reports-list',
                            component: ReportList,
                            meta: {
                                title: 'Reports',
                                requiresAuthentication: true,
                                ignoreBreadcrumb: true,
                                requiresAnyRole: [
                                    Permissions.SYSTEM,
                                    Permissions.ADMIN
                                ],
                            },
                        },
                        {
                            path: 'create',
                            name: 'create-report',
                            component: ViewReport,
                            meta: {
                                title: 'Create',
                                requiresAuthentication: true,
                                requiresAnyRole: [
                                    Permissions.SYSTEM,
                                    Permissions.ADMIN
                                ],
                            },
                        },
                        {
                            path: ':id',
                            name: 'view-report',
                            component: ViewReport,
                            meta: {
                                title: 'Report Details',
                                requiresAuthentication: true,
                                requiresAnyRole: [
                                    Permissions.SYSTEM,
                                    Permissions.ADMIN
                                ],
                            },
                        }
                    ],
                }
            ],
        },
        // Email links
        {
            path: '/:customerName/:siteName/enquiries/:id',
            name: 'view-enquiry-withCustomerSite',
            beforeEnter: (to, from, next) => {
                // Note(Seb): This is used for email notifications. We use this url to set the current customer/site and then navigate to the desired link
                // Avoids a 403 page due to the currently selected customer/site not matching the customerId/siteId of the resource
                const customers = store.getters['customer/customers'] as Array<Customer>;
                const customerNameKebab = to.params.customerName || null;
                const siteNameKebab = to.params.siteName || null;

                const customerMatch = customers.find((c) => kebabcase(c.name) === customerNameKebab) || null;
                const siteMatch = customerMatch?.sites.find((s) => kebabcase(s.name) === siteNameKebab) || null;

                if (customerMatch !== null && siteMatch !== null) {
                    store.commit('user/setCurrentCustomer', customerMatch);
                    store.commit('user/setCurrentSite', siteMatch);
                }

                next({ name: 'view-enquiry', params: { id: to.params.id } });
            },
        },
        {
            path: '/:customerName/:siteName/quotes/:id',
            name: 'view-quote-withCustomerSite',
            beforeEnter: (to, from, next) => {
                // Note(Seb): This is used for email notifications. We use this url to set the current customer/site and then navigate to the desired link
                // Avoids a 403 page due to the currently selected customer/site not matching the customerId/siteId of the resource
                const customers = store.getters['customer/customers'] as Array<Customer>;
                const customerNameKebab = to.params.customerName || null;
                const siteNameKebab = to.params.siteName || null;

                const customerMatch = customers.find((c) => kebabcase(c.name) === customerNameKebab) || null;
                const siteMatch = customerMatch?.sites.find((s) => kebabcase(s.name) === siteNameKebab) || null;

                if (customerMatch !== null && siteMatch !== null) {
                    store.commit('user/setCurrentCustomer', customerMatch);
                    store.commit('user/setCurrentSite', siteMatch);
                }

                next({ name: 'view-quote', params: { id: to.params.id } });
            },
        },
        {
            path: '/:customerName/:siteName/orders/:id',
            name: 'view-order-withCustomerSite',
            beforeEnter: (to, from, next) => {
                // Note(Seb): This is used for email notifications. We use this url to set the current customer/site and then navigate to the desired link
                // Avoids a 403 page due to the currently selected customer/site not matching the customerId/siteId of the resource
                const customers = store.getters['customer/customers'] as Array<Customer>;
                const customerNameKebab = to.params.customerName || null;
                const siteNameKebab = to.params.siteName || null;

                const customerMatch = customers.find((c) => kebabcase(c.name) === customerNameKebab) || null;
                const siteMatch = customerMatch?.sites.find((s) => kebabcase(s.name) === siteNameKebab) || null;

                if (customerMatch !== null && siteMatch !== null) {
                    store.commit('user/setCurrentCustomer', customerMatch);
                    store.commit('user/setCurrentSite', siteMatch);
                }

                next({ name: 'view-order', params: { id: to.params.id } });
            },
        },
        {
            path: '/:customerName/:siteName/approvals/:id',
            name: 'view-approval-withCustomerSite',
            beforeEnter: (to, from, next) => {
                // Note(Seb): This is used for email notifications. We use this url to set the current customer/site and then navigate to the desired link
                // Avoids a 403 page due to the currently selected customer/site not matching the customerId/siteId of the resource
                const customers = store.getters['customer/customers'] as Array<Customer>;
                const customerNameKebab = to.params.customerName || null;
                const siteNameKebab = to.params.siteName || null;

                const customerMatch = customers.find((c) => kebabcase(c.name) === customerNameKebab) || null;
                const siteMatch = customerMatch?.sites.find((s) => kebabcase(s.name) === siteNameKebab) || null;

                if (customerMatch !== null && siteMatch !== null) {
                    store.commit('user/setCurrentCustomer', customerMatch);
                    store.commit('user/setCurrentSite', siteMatch);
                }

                next({ name: 'view-approval', params: { id: to.params.id } });
            },
        },
        // Not found
        {
            path: '/not-found',
            name: 'not-found',
            component: NotFound,
            meta: {
                title: 'Page not found',
                requiresAuthentication: false,
            },
        },
        // Forbidden
        {
            path: '/forbidden',
            name: 'forbidden',
            component: Forbidden,
            meta: {
                title: 'Forbidden',
                requiresAuthentication: false,
            },
        },
        // No customer or site access
        {
            path: '/access-denied',
            name: 'access-denied',
            component: AccessDenied,
            meta: {
                title: 'Access denied',
                requiresAuthentication: false,
            },
        },
        // Catch all
        {
            path: '*',
            redirect: '/not-found',
        }
    ],
};

const router = new Router(routerOptions);

router.afterEach((to, from) => {
    document.title = `${to.meta.title} - Hayley Non Stock Enquiry`;
});

router.beforeEach(async (to, from, next) => {
    if (store.getters['user/user'] == null && to.meta.requiresAuthentication) {
        next({ name: 'login' });
        return;
    }

    if (to.name === 'login' && store.getters['user/user'] != null) {
        next({ name: 'home' });
        return;
    }

    if (to.meta.requiresAuthentication) {
        const hasAccess = store.getters['user/hasAccess'] as boolean;
        if (!hasAccess) {
            next({ name: 'access-denied' });
            return;
        }
    }

    if (to.meta.requiresAuthentication && to.meta.requiresAnyRole && to.meta.requiresAnyRole.length > 0) {
        const check = store.getters['user/hasAnyPermission'];

        if (!check(to.meta.requiresAnyRole)) {
            next({ name: 'forbidden' });
            return;
        }
    }

    next();
});

export default router;
